<template>
  <div v-if="configuration.EmissionAndConsumption">
    <strong>{{ i18n.t('/Emission And Consumption/WLTP Label') }}</strong>
    <dl class="consumption-block">
      <dt>{{ i18n.t('/Emission And Consumption/Consumption') }}</dt>
      <dd>{{ configuration.EmissionAndConsumption.WltpFuelConsumption }}</dd>

      <dt>{{ i18n.t('/Emission And Consumption/CO2 Emissions') }}</dt>
      <dd>
        {{ configuration.EmissionAndConsumption.WltpCO2Emission }}
        <span
          v-if="configuration.EmissionAndConsumption.IsFallback"
          class="co2-fallback-icon"
          :title="`CO2 OKAPI (${configuration.EmissionAndConsumption.Co2SourceDate})`"
          >ⓘ</span
        >
      </dd>

      <template v-if="isElectroCar">
        <dt>{{ i18n.t('/Emission And Consumption/Range') }}</dt>
        <dd>{{ configuration.EmissionAndConsumption.WltpRange }}</dd>
      </template>
    </dl>
    <div v-if="configuration.Bpm.Value != undefined">
      <div class="bpm-block">
        <div>
          <strong>
            {{ i18n.t('/Emission And Consumption/BPM Value Label') }}
            {{ i18n.n(configuration.Bpm.Value, 'currency') }}
          </strong>
        </div>
        <template v-if="showNoBpmCheckbox">
          <div>
            <form-checkbox
              id="checkbox-no-bpm"
              :title="i18n.t('/Emission And Consumption/No BPM')"
              :disabled="quoteStatusIsSalesOrder"
            >
              <input
                id="checkbox-no-bpm"
                v-model.lazy="noBpm"
                type="checkbox"
                :disabled="quoteStatusIsSalesOrder"
                @change="setNoBpm"
              />
            </form-checkbox>
          </div>
          <div v-if="!bpmCalculated">
            {{ i18n.t('/Emission And Consumption/Original BPM') }}:
            {{ i18n.n(configuration.OriginalBpm, 'currency') }}
          </div>
        </template>
      </div>
      <small v-if="showNoBpmCheckbox && !bpmCalculated">
        {{ i18n.t('/Emission And Consumption/BPM Disclaimer') }}
      </small>
      <br />
      <small>
        {{ i18n.t('/Emission And Consumption/BPM Based WLTP Label') }}
      </small>
    </div>
    <div v-if="configuration.Bpm.IsCorrupt" class="incorrect-bpm-warning">
      <strong>
        {{ i18n.t('/Emission And Consumption/Corrupted BPM') }}
      </strong>
    </div>

    <confirmation-prompt
      id="no-bpm-prompt"
      :show="showNoBpmPrompt"
      :info-text="i18n.t('/Emission And Consumption/No BPM Confirmation Text')"
      :confirm-button-text="i18n.t('/common/yes')"
      :cancel-button-text="i18n.t('/common/no')"
      :title="i18n.t('/Emission And Consumption/No BPM Confirmation Title')"
      @confirm="updateNoBpm"
      @cancel="cancelNoBpm"
      @close="closeNoBpmPrompt"
    >
    </confirmation-prompt>
  </div>
</template>

<script>
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import ConfirmationPrompt from '@/components/generic/presentational/molecules/confirmationPrompt/ConfirmationPrompt.vue'
import { mapGetters } from 'vuex'

export default {
  components: { ConfirmationPrompt, FormCheckbox },
  inject: ['enableNoBpm'],
  props: {
    configuration: {
      type: Object,
      default: null,
    },
  },
  emits: ['updateCalculateBpm'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      noBpm: null,
      showNoBpmPrompt: false,
      unWatch: null,
    }
  },
  computed: {
    ...mapGetters('quote', ['quoteStatusIsSalesOrder', 'bpmCalculated']),
    isElectroCar() {
      if (this.configuration.EmissionAndConsumption.WltpRange !== null) {
        return true
      }
      return false
    },
    showNoBpmCheckbox() {
      return this.enableNoBpm
    },
  },
  created() {
    // When a new quote is created there is a delay in initializing the store
    // This updates the initial noBpm value after the quote is initialized
    this.unWatch = this.$watch(
      'bpmCalculated',
      (bpmCalculated) => {
        if (this.noBpm !== null && this.unWatch) {
          // Using immediate means that the unwatch can only be called in the next callback because otherwise it's not initialised yet
          this.unWatch()
        } else if (bpmCalculated !== undefined) {
          this.noBpm = !bpmCalculated
        }
      },
      { immediate: true }
    )
  },
  methods: {
    setNoBpm() {
      if (this.noBpm) {
        this.showNoBpmPrompt = true
      } else {
        this.updateNoBpm()
      }
    },
    updateNoBpm() {
      this.$emit('updateCalculateBpm', !this.noBpm)
    },
    cancelNoBpm() {
      this.noBpm = false
    },
    closeNoBpmPrompt(isAfterAction) {
      if (!isAfterAction) {
        this.cancelNoBpm()
      }
      this.showNoBpmPrompt = false
    },
  },
}
</script>
<style lang="scss" scoped>
.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.consumption-block {
  @extend .grid-container;
  margin: 10px;

  dt {
    grid-column: 1;
  }

  dd {
    grid-column: 2;
  }
}

.bpm-block {
  @extend .grid-container;
  margin: 10px 0;
}

.co2-fallback-icon {
  cursor: help;
  color: $color-info;
  font-style: normal;
  margin-left: 4px;
}

.incorrect-bpm-warning {
  background-color: $color-warning;
  color: $white;
  padding: 5px;
  box-shadow: $shadow-sm;
  border-radius: 1px;
}
</style>
